import React, { useCallback, useMemo, useRef } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useController, useForm } from 'react-hook-form'
import { z } from 'zod'
import { Icon, l, TextInputV2 } from '@manychat/manyui'

import { InputConfig } from 'apps/aiTextImprover/lib/types'
import { TextImproverValidationMessages } from 'apps/aiTextImprover/lib/validation'
import { useKeyDown } from 'common/core/coreHooks'

import cm from './Input.module.css'

interface InputProps {
  config: InputConfig
  onSubmit: (text: string) => void
}

export const Input = ({ config: { label, keyCode, validation }, onSubmit }: InputProps) => {
  const validationMessage = TextImproverValidationMessages.getMinMaxValidationMessage(
    validation.min,
    validation.max,
  )

  const resolver = useMemo(
    () =>
      zodResolver(
        z.object({
          value: z
            .string()
            .trim()
            .min(validation.min, validationMessage)
            .max(validation.max, validationMessage),
        }),
      ),
    [validation.max, validation.min, validationMessage],
  )

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      value: '',
    },
    resolver,
  })

  const { field } = useController({
    control,
    name: 'value',
  })

  const inputRef = useRef<HTMLInputElement | null>(null)
  const isManuallyFocusing = useRef(false)

  const focusInput = useCallback(() => {
    if (!inputRef.current) {
      return
    }

    isManuallyFocusing.current = true

    inputRef.current.focus()

    setTimeout(() => {
      isManuallyFocusing.current = false
    }, 100)
  }, [])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // This hack is necessary to fix a bug, when input is manually focused by a shortcut and
    // a symbol from a shortcut is saved into input's value.
    if (!isManuallyFocusing.current) {
      field.onChange(event)
    }
  }

  useKeyDown({
    key: keyCode,
    onPress: focusInput,
  })

  return (
    <div className={cm.inputWrapper}>
      <form onSubmit={handleSubmit(({ value }) => onSubmit(value))}>
        <div>
          <TextInputV2
            inputRef={inputRef}
            placeholder={l.getString(label)}
            ariaLabel={l.getString(label)}
            type="text"
            onBlur={field.onBlur}
            onChange={handleChange}
            name={field.name}
            value={field.value}
            invalid={Boolean(errors.value?.message)}
            errorText={errors.value?.message}
            className={cm.input}
            data-test-id="ai-text-improver/custom-option-input"
          />
        </div>

        <button
          type="submit"
          className={cm.button}
          data-test-id="ai-text-improver/custom-option-input-submit-button"
        >
          <span className={cm.button__keyCode}>
            <Icon.ReturnKey size={16} />
          </span>
        </button>
      </form>
    </div>
  )
}
